import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Table, Button, EmptyState, LoadingSkeleton, Pagination, Modal, FilterBar, FilterSelect } from './ui';
import { UserGroupIcon, PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon, ArrowUturnLeftIcon, ArchiveBoxIcon, EnvelopeIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { hasAnyRole } from '../utils/roles';
import { safeErrorMessage } from '../utils/apiError';
import SetupLinkNotice from './accounts/SetupLinkNotice';

const EDIT_ROLES = ['administrator', 'principal', 'head_of_academics', 'teacher'];
const DELETE_ROLES = ['administrator', 'principal'];
// Same roles that may create accounts on the backend (POST /students/<id>/invite-guardian)
const INVITE_ROLES = ['administrator', 'principal'];
// Same roles as backend/student_import.py's IMPORT_ROLES
const IMPORT_ROLES = ['administrator', 'principal', 'head_of_academics'];

function Students({ token, user }) {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showRemoved, setShowRemoved] = useState(false);
  const [classFilter, setClassFilter] = useState('');
  const [streamFilter, setStreamFilter] = useState('');
  const [clubFilter, setClubFilter] = useState('');
  const [houseFilter, setHouseFilter] = useState('');
  const [clubs, setClubs] = useState([]);
  // null = no club chosen; otherwise the ids of that club's members
  const [clubMemberIds, setClubMemberIds] = useState(null);
  const clubsEnabled = user?.features?.clubs !== false;
  const pageSize = 20;
  const canEdit = hasAnyRole(user, EDIT_ROLES);
  const canDelete = hasAnyRole(user, DELETE_ROLES);
  const canInvite = hasAnyRole(user, INVITE_ROLES);
  const canImport = hasAnyRole(user, IMPORT_ROLES);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [inviting, setInviting] = useState(false);

  const openInvite = (student) => {
    setInviteTarget(student);
    setInviteResult(null);
    setInviteError('');
  };

  const handleInvite = async () => {
    setInviting(true);
    setInviteError('');
    try {
      const response = await fetch(`/api/v1/students/${inviteTarget.id}/invite-guardian`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setInviteResult(await response.json());
      } else {
        setInviteError(await safeErrorMessage(response, 'Failed to invite guardian'));
      }
    } catch {
      setInviteError('Network error. Please check your connection and try again.');
    } finally {
      setInviting(false);
    }
  };

  const fetchStudents= useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/students${showRemoved ? '?status=removed' : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setStudents(await response.json());
      } else {
        setError('Failed to load students');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [token, showRemoved]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  useEffect(() => {
    if (!clubsEnabled) return;
    fetch('/api/v1/clubs', { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => (response.ok ? response.json() : []))
      .then(setClubs)
      .catch(() => {});
  }, [token, clubsEnabled]);

  useEffect(() => {
    if (!clubFilter) {
      setClubMemberIds(null);
      return undefined;
    }
    let cancelled = false;
    fetch(`/api/v1/clubs/${clubFilter}/members`, { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => (response.ok ? response.json() : []))
      .then((members) => { if (!cancelled) setClubMemberIds(new Set(members.map((m) => m.student_id))); })
      .catch(() => { if (!cancelled) setClubMemberIds(new Set()); });
    return () => { cancelled = true; };
  }, [clubFilter, token]);

  const handleDelete = async (student) => {
    if (!window.confirm(`Remove ${student.full_name} from the student directory? This can be undone from "Removed Students".`)) return;
    try {
      const response = await fetch(`/api/v1/students/${student.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setStudents((prev) => prev.filter((s) => s.id !== student.id));
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to remove student');
      }
    } catch {
      setError('Network error');
    }
  };

  const handleRestore = async (student) => {
    try {
      const response = await fetch(`/api/v1/students/${student.id}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setStudents((prev) => prev.filter((s) => s.id !== student.id));
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to restore student');
      }
    } catch {
      setError('Network error');
    }
  };

  const classOptions = [...new Set(students.map((s) => s.class_name).filter(Boolean))].sort();
  const streamOptions = [...new Set(
    students.filter((s) => !classFilter || s.class_name === classFilter).map((s) => s.stream).filter(Boolean)
  )].sort();
  const houseOptions = [...new Set(students.map((s) => s.house).filter(Boolean))].sort();
  const filtersActive = Boolean(classFilter || streamFilter || clubFilter || houseFilter);
  const clearFilters = () => { setClassFilter(''); setStreamFilter(''); setClubFilter(''); setHouseFilter(''); };

  const filtered = students
    .filter((s) =>
      !search || s.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.class_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.national_id?.toLowerCase().includes(search.toLowerCase())
    )
    .filter((s) => !classFilter || s.class_name === classFilter)
    .filter((s) => !streamFilter || s.stream === streamFilter)
    .filter((s) => !houseFilter || s.house === houseFilter)
    .filter((s) => !clubMemberIds || clubMemberIds.has(s.id))
    .sort((a, b) => (a.surname || a.full_name || '').localeCompare(b.surname || b.full_name || '') || (a.full_name || '').localeCompare(b.full_name || ''));

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [search, showRemoved, classFilter, streamFilter, clubFilter, houseFilter]);

  const columns = [
    { header: 'Name', render: (row) => <strong>{row.full_name}</strong> },
    { header: 'National ID', accessor: 'national_id', render: (row) => row.national_id || '—' },
    { header: 'Class', accessor: 'class_name', render: (row) => row.class_name || '—' },
    { header: 'Stream', accessor: 'stream', render: (row) => row.stream || '—' },
    { header: 'House', accessor: 'house', render: (row) => row.house || '—' },
    { header: 'Phone', accessor: 'phone', render: (row) => row.phone || '—' },
    { header: 'Guardian', accessor: 'guardian_name', render: (row) => row.guardian_name || '—' },
  ];

  if (showRemoved) {
    columns.push({
      header: 'Actions',
      render: (row) => (
        <Button variant="secondary" size="sm" onClick={() => handleRestore(row)}>
          <ArrowUturnLeftIcon style={{ width: 14, height: 14 }} />
          Restore
        </Button>
      ),
    });
  } else if (canEdit || canDelete) {
    columns.push({
      header: 'Actions',
      render: (row) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {canInvite && row.guardian_email && (
            <Button variant="ghost" size="sm" onClick={() => openInvite(row)} title="Give the guardian a parent login" aria-label={`Invite guardian of ${row.full_name}`}>
              <EnvelopeIcon style={{ width: 14, height: 14 }} />
            </Button>
          )}
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => navigate(`/students/edit/${row.id}`)}>
              <PencilIcon style={{ width: 14, height: 14 }} />
            </Button>
          )}
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(row)}>
              <TrashIcon style={{ width: 14, height: 14, color: 'var(--color-danger)' }} />
            </Button>
          )}
        </div>
      ),
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>
          {showRemoved ? 'Removed Students' : 'Student Directory'}
        </h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {!showRemoved && (
            <div style={{ position: 'relative' }}>
              <MagnifyingGlassIcon style={{ position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'var(--color-text-muted)' }} />
              <input
                type="text"
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ padding: '0.5rem 0.5rem 0.5rem 2rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontSize: '0.85rem', width: 200, background: 'var(--color-surface)' }}
              />
            </div>
          )}
          {canDelete && (
            <Button variant="secondary" size="sm" onClick={() => setShowRemoved((v) => !v)}>
              <ArchiveBoxIcon style={{ width: 16, height: 16 }} />
              {showRemoved ? 'Back to Directory' : 'Removed Students'}
            </Button>
          )}
          {!showRemoved && canImport && (
            <Link to="/students/import">
              <Button variant="secondary" size="sm">
                <ArrowUpTrayIcon style={{ width: 16, height: 16 }} />
                Import students
              </Button>
            </Link>
          )}
          {!showRemoved && canEdit && (
            <Link to="/students/new">
              <Button variant="primary" size="sm">
                <PlusIcon style={{ width: 16, height: 16 }} />
                Add Student
              </Button>
            </Link>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {!showRemoved && (
        <FilterBar shown={filtered.length} total={students.length} onClear={filtersActive ? clearFilters : undefined}>
          <FilterSelect label="Class" value={classFilter} onChange={(value) => { setClassFilter(value); setStreamFilter(''); }} options={classOptions} allLabel="All classes" />
          <FilterSelect label="Stream" value={streamFilter} onChange={setStreamFilter} options={streamOptions} allLabel="All streams" />
          {houseOptions.length > 0 && (
            <FilterSelect label="House" value={houseFilter} onChange={setHouseFilter} options={houseOptions} allLabel="All houses" />
          )}
          {clubsEnabled && (
            <FilterSelect label="Club"value={clubFilter} onChange={setClubFilter} options={clubs.map((club) => ({ value: String(club.id), label: club.name }))} allLabel="All clubs" />
          )}
        </FilterBar>
      )}

      <Card>
        {loading ? (
          <LoadingSkeleton type="text" rows={8} />
        ) : filtered.length === 0 ? (
          <EmptyState
            title={showRemoved ? 'No removed students' : ((search || filtersActive) ? 'No matches found' : 'No students yet')}
            message={showRemoved ? 'Removed students will appear here and can be restored.' : ((search || filtersActive) ? 'Try a different search term or filter.' : (canEdit ? 'Click "Add Student" to get started.' : 'No students to show yet.'))}
            icon={<UserGroupIcon />}
            action={!showRemoved && !search && !filtersActive && canEdit && <Link to="/students/new"><Button variant="primary" size="sm"><PlusIcon style={{ width: 16, height: 16 }} />Add Student</Button></Link>}
          />
        ) : (
          <>
            <Table columns={columns} data={paginated} />
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </Card>

      <Modal isOpen={Boolean(inviteTarget)} onClose={() => setInviteTarget(null)} title="Parent portal access">
        {inviteTarget && (
          <div>
            {inviteError && <div className="alert alert-error">{inviteError}</div>}
            {!inviteResult ? (
              <>
                <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
                  Give <strong>{inviteTarget.guardian_name || 'the guardian'}</strong> ({inviteTarget.guardian_email}) a parent login for{' '}
                  <strong>{inviteTarget.full_name}</strong>. Any other student with the same guardian email is linked to the same account. If no account exists yet, one is created and they are emailed a link to set their password.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button variant="primary" onClick={handleInvite} disabled={inviting}>{inviting ? 'Working…' : 'Give access'}</Button>
                  <Button variant="secondary" onClick={() => setInviteTarget(null)}>Cancel</Button>
                </div>
              </>
            ) : (
              <>
                <div className="alert alert-success">
                  {inviteResult.account_created
                    ? `Parent account "${inviteResult.parent.username}" created for ${inviteResult.parent.full_name}.`
                    : `Linked to the existing account "${inviteResult.parent.username}" (${inviteResult.parent.full_name}).`}
                  {' '}Children on this account: {inviteResult.children.map((child) => child.full_name).join(', ')}.
                </div>
                <SetupLinkNotice invite={inviteResult.invite} email={inviteResult.parent.email} />
                <Button variant="primary" onClick={() => setInviteTarget(null)}>Done</Button>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Students;
